import { Component, Output, EventEmitter, inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MediaMatcher } from '@angular/cdk/layout';
import { MaterialModule } from '../../shared/material/material.module';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthCustomService } from '../../security/auth-custom.service';
import { UserPreferencesCacheService } from '../../shared/services/user-preferences-cache.service';
import { ProfileProviderPFStateService } from '../../shared/services/profile-provider-pf-state.service';
import { ProfileProviderPJStateService } from '../../shared/services/profile-provider-pj-state.service';
import { ProfileProviderPF, ProfileProviderPJ, UserRef, PlanStatusInfo } from '../../core/generated';
import { Subject, takeUntil, firstValueFrom, combineLatest } from 'rxjs';
import { PlanStatusService, PlanStatusCacheInfo } from '../../shared/services/plan-status-cache.service';
import { Feature } from '../../shared/config/package-features.config';
import { EncryptionUtil } from '../../shared/utils/encryption.util';

@Component({
    selector: 'app-sidebar',
    standalone: true,
    imports: [MaterialModule],
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {

  // Services
  public authCustomService = inject(AuthCustomService);
  private userPreferencesCacheService = inject(UserPreferencesCacheService);
  private profileProviderPFStateService = inject(ProfileProviderPFStateService);
  private profileProviderPJStateService = inject(ProfileProviderPJStateService);
  private planStatusService = inject(PlanStatusService);
  private cdr = inject(ChangeDetectorRef);

  @Output() toggleSideBarForMe: EventEmitter<void> = new EventEmitter();

  user!: UserRef;
  profileProviderPF!: ProfileProviderPF;
  profileProviderPJ: ProfileProviderPJ[] = [];
  mobileQuery: MediaQueryList;

  /** Cache local dos planos — atualizado reativamente via planStatuses$ */
  private planStatuses: PlanStatusCacheInfo[] = [];

  private readonly destroy$ = new Subject<void>();

  readonly Feature = Feature;

  constructor(private media: MediaMatcher, public router: Router, private route: ActivatedRoute) {
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
  }

  async ngOnInit() {
    await this.loadUserAndProfiles();

    // Assina planStatuses$ — qualquer mudança nos planos força re-render do template
    this.planStatusService.planStatuses$
      .pipe(takeUntil(this.destroy$))
      .subscribe((statuses) => {
        this.planStatuses = statuses;
        this.cdr.markForCheck();
      });

    await this.planStatusService.loadForCurrentUser();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadUserAndProfiles(): Promise<void> {
    const user = await firstValueFrom(this.userPreferencesCacheService.getUser());
    if (!user) return;

    this.user = user;

    if (user.email) {
      this.loadProfiles(user.email);
    }
  }

  private loadProfiles(email: string): void {
    // Combina os dois observables num único fluxo — qualquer mudança em perfis aciona o cdr
    combineLatest([
      this.profileProviderPFStateService.profileProviderPF$,
      this.profileProviderPJStateService.profileProviderPJ$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([pf, pj]) => {
        this.profileProviderPF = pf as ProfileProviderPF;
        this.profileProviderPJ = pj ?? [];
        this.cdr.markForCheck();
      });

    // Dispara o carregamento
    this.profileProviderPFStateService.loadByEmail(email);
    this.profileProviderPJStateService.forceRefreshByEmail(email);
  }

  toggleSideBar() {
    if (this.mobileQuery.matches) {
      this.toggleSideBarForMe.emit();
    }
  }

  isAuthenticated(): boolean {
    return this.authCustomService.hasValidToken();
  }

  hasProviderPF(): boolean {
    return !!this.profileProviderPF?.id;
  }

  hasProviderPJ(): boolean {
    return this.profileProviderPJ?.length > 0;
  }

  // ── Métodos de acesso — leem de planStatuses (local, já atualizado reativamente) ──

  canAccess(cpfCnpj: string, feature?: Feature | Feature[]): boolean {
    return this.planStatusService.canAccessFeature(cpfCnpj, feature).hasAccess;
  }

  hasFeatureAccess(cpfCnpj: string, feature: Feature | Feature[]): boolean {
    return this.planStatusService.hasFeatureAccess(cpfCnpj, feature);
  }

  getProfileType(cpfCnpj: string): string {
    return this.planStatusService.getProfileType(cpfCnpj);
  }

  hasAnyActivePlan(): boolean {
    return this.planStatusService.hasAnyActivePlan();
  }

  hasAnyPlan(): boolean {
    return this.hasAnyActivePlan();
  }

  isProvider(cpfCnpj: string): boolean {
    return this.planStatusService.isProvider(cpfCnpj);
  }

  isContractor(cpfCnpj: string): boolean {
    return this.planStatusService.isContractor(cpfCnpj);
  }

  findPlanStatusBycpfCnpj(cpfCnpj: string): PlanStatusInfo | null {
    if (!cpfCnpj) return null;
    return this.planStatusService.findByCpfCnpj(cpfCnpj);
  }

  isInMyAccountSection(): boolean {
    return this.router.url.includes('/my-account');
  }

  extractIdFromUrl(url: string): string | null {
    let snapshot: any = this.route.snapshot;
    while (snapshot) {
      if (snapshot.paramMap?.has('id')) {
        return snapshot.paramMap.get('id');
      }
      snapshot = snapshot.firstChild;
    }
    const match = url.match(/\/providerPJ\/([^\/]+)/) || url.match(/\/providerPF\/([^\/]+)/);
    return match ? match[1] : null;
  }

  encriptCpfcnpj(cpfcnpj: string): any | string {
    return EncryptionUtil.encrypt(cpfcnpj);
  }
}
